import { nanoid } from 'nanoid';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { TransactionAnalyticsRecord } from '@sola/sdg-sdk/social-proof/hooks/use-transaction-subscription.ts';
import { useTransactionSubscription } from '@sola/sdg-sdk/social-proof/hooks/use-transaction-subscription.ts';
import type { TransactionAnalyticsItem } from '@sola/sdg-sdk/social-proof/lib/schema.ts';
import { extractSponsorshipItems, hasValidBeneficiaryName, isValidTransaction } from '@sola/sdg-sdk/social-proof/lib/transaction-filters.ts';

/**
 * Data needed to render a single toast notification.
 */
export interface ToastData {
  /** Unique ID for React key and tracking */
  id: string;
  /** Child's name */
  beneficiaryName: string;
  /** Child's image URL (null triggers placeholder) */
  beneficiaryImageUrl: string | null;
  /** Sponsor's first name (null → "Someone") */
  firstName: string | null;
  /** Sponsor's state (null → omit location) */
  state: string | null;
}

/** Transforms a sponsorship item + parent transaction into ToastData */
const toToastData = (item: TransactionAnalyticsItem, transaction: TransactionAnalyticsRecord): ToastData => ({
  id: nanoid(),
  beneficiaryName: item.beneficiaryName!,
  beneficiaryImageUrl: item.beneficiaryImageUrl ?? null,
  firstName: transaction.firstName ?? null,
  state: transaction.state ?? null
});
export interface SocialProofToastConfig {
  /** AppSync HTTP endpoint URL */
  appSyncUrl: string;
  /** AppSync API key */
  apiKey: string;
  /** Optional source code filter for the subscription */
  sourceCode?: string;
  /** Auto-dismiss duration in ms (default: 5000) */
  dismissDuration?: number;
  /** Whether the toast system is enabled (default: true) */
  enabled?: boolean;
}
export interface UseSocialProofToastsResult {
  /** The currently visible toast, or null if none */
  currentToast: ToastData | null;
  /** Whether the toast is in the dismissing animation state */
  isDismissing: boolean;
  /** Trigger the dismiss animation */
  handleDismiss: () => void;
  /** Callback for when the dismiss animation completes */
  handleAnimationEnd: () => void;
  /** Whether the WebSocket connection is active */
  isConnected: boolean;
}
export const useSocialProofToasts = (config: SocialProofToastConfig): UseSocialProofToastsResult => {
  const {
    appSyncUrl,
    apiKey,
    sourceCode,
    dismissDuration = 5000,
    enabled = true
  } = config;
  const [currentToast, setCurrentToast] = useState<ToastData | null>(null);
  const [isDismissing, setIsDismissing] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentToastRef = useRef<ToastData | null>(null);
  currentToastRef.current = currentToast;
  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Handle each incoming transaction — show if idle, otherwise drop
  const handleTransaction = useCallback((transaction: TransactionAnalyticsRecord) => {
    if (currentToastRef.current !== null) {
      return;
    }
    if (!isValidTransaction(transaction)) {
      return;
    }
    const items = extractSponsorshipItems(transaction.items);
    for (const item of items) {
      if (hasValidBeneficiaryName(item)) {
        setCurrentToast(toToastData(item, transaction));
        return;
      }
    }
  }, []);
  const {
    isConnected
  } = useTransactionSubscription({
    appSyncUrl,
    apiKey,
    sourceCode,
    enabled,
    onTransaction: handleTransaction
  });

  // Auto-dismiss flow:
  // 1. Toast appears → timer starts (dismissDuration ms)
  // 2. Timer fires → isDismissing=true → CSS fade-out animation plays (300ms)
  // 3. onAnimationEnd fires → toast removed, isDismissing reset
  // Total visible time = dismissDuration + fade-out animation duration
  useEffect(() => {
    const toastId = currentToast?.id;
    if (!toastId) {
      return;
    }
    clearTimer();
    timerRef.current = setTimeout(() => {
      setIsDismissing(true);
    }, dismissDuration);
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [currentToast?.id, dismissDuration, clearTimer]);
  const handleAnimationEnd = useCallback(() => {
    setIsDismissing(false);
    setCurrentToast(null);
  }, []);
  const handleDismiss = useCallback(() => {
    clearTimer();
    setIsDismissing(true);
  }, [clearTimer]);
  return {
    currentToast,
    isDismissing,
    handleDismiss,
    handleAnimationEnd,
    isConnected
  };
};